/**
 * worker-run-client — run checkpoint code on a worker with no job
 * (FEATURE-20261027-runner-runs-checkpoint-without-a-job).
 *
 * The browser asks the worker's job runner directly: registry.v1.FindWorkerRunner names its mailbox,
 * and jobrunner.v1.RunCheckpoint is a streaming call the runner hands to the leaf it launches. The leaf
 * answers this browser itself: first "running" with the checkpoint process uid, which the engine mints,
 * then what the program prints, then how it ended. With that uid the run can be followed (its console,
 * ProcessStatus, Waiting Programs) after the stream ends. The Go twin is godev pkg/workerrun.
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, asMailboxAddress, MailboxAddress } from "../hermesclient/types";

/**
 * One element of a RunCheckpoint answer, as the leaf sends it (protojson, camelCase). The event is a
 * oneof: exactly one of running, output, finished, paused, failed or interrupted is set.
 */
export interface RunCheckpointEvent {
  processUid?: string;
  hostProcessRunUid?: string;
  running?: Record<string, never>;
  output?: { text?: string };
  finished?: { resultJson?: string };
  paused?: { checkpointUid?: string; needsGrant?: string };
  failed?: { message?: string };
  interrupted?: { message?: string };
}

/** Start a function (or main) of a fileset revision. */
export interface RunCheckpointStart {
  filesetUid: string;
  filesetRevisionUid: string;
  mainModule?: string;
  function?: string;
  inputJson?: string;
}

/** Continue a saved program. */
export interface RunCheckpointResume {
  checkpointUid: string;
  valueJson?: string;
}

/** What to run: a start or a resume (a oneof), and how. */
export type RunCheckpointRequest = ({ start: RunCheckpointStart; resume?: never } | { resume: RunCheckpointResume; start?: never }) & {
  executionUser?: string;
  jobUid?: string;
  reason?: string;
};

/** How a run on a worker ended, folded from its events. */
export interface WorkerRunOutcome {
  processUid: string;
  host: string; // the processrun of the a8 checkpoint exec that hosted the run
  state: string; // finished | paused | failed | interrupted; "" when the stream ended without saying
  resultJson: string;
  message: string;
  checkpointUid: string;
  needsGrant: string;
  output: string;
}

/** foldEvent adds one event to the outcome so far; pure, for the tests. */
export function foldEvent(out: WorkerRunOutcome, ev: RunCheckpointEvent): WorkerRunOutcome {
  const next = { ...out };
  if (ev.processUid) next.processUid = ev.processUid;
  if (ev.hostProcessRunUid) next.host = ev.hostProcessRunUid;
  if (ev.output) next.output += ev.output.text ?? "";
  else if (ev.finished) Object.assign(next, { state: "finished", resultJson: ev.finished.resultJson ?? "" });
  else if (ev.paused) Object.assign(next, { state: "paused", checkpointUid: ev.paused.checkpointUid ?? "", needsGrant: ev.paused.needsGrant ?? "" });
  else if (ev.failed) Object.assign(next, { state: "failed", message: ev.failed.message ?? "" });
  else if (ev.interrupted) Object.assign(next, { state: "interrupted", message: ev.interrupted.message ?? "" });
  return next;
}

async function registryCall<TResp>(endPoint: string, request: Record<string, unknown>): Promise<TResp> {
  const to = await nameMappingService.resolve(asLogicalMailbox("registry"));
  const resp = await GlobalClient.get().rawRpcCall({
    to,
    endPoint,
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  if (resp.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const info = resp.header?.rpcHeader?.errorInfo;
    throw new Error(info?.message || info?.errorNode?.frame?.message || `${endPoint} failed`);
  }
  if (!resp.data || resp.data.length === 0) return {} as TResp;
  return JSON.parse(new TextDecoder().decode(resp.data)) as TResp;
}

/** The mailbox of a worker's live job runner; throws, naming why, when there is none. */
export async function findWorkerRunner(workerUid: string): Promise<MailboxAddress> {
  const resp = await registryCall<{ found?: boolean; mailboxAddress?: string; message?: string }>("registry.v1.FindWorkerRunner", {
    workerUid,
  });
  if (!resp.found || !resp.mailboxAddress) {
    throw new Error(resp.message || `no live job runner for worker ${workerUid}`);
  }
  return asMailboxAddress(resp.mailboxAddress);
}

/**
 * Run req on the worker and follow the answer to its end. onEvent sees every event as it arrives (the
 * first carries the process uid).
 */
export async function runOnWorker(
  workerUid: string,
  req: RunCheckpointRequest,
  onEvent?: (ev: RunCheckpointEvent) => void,
): Promise<WorkerRunOutcome> {
  const runner = await findWorkerRunner(workerUid);
  const request: RunCheckpointRequest = { ...req };
  const call = GlobalClient.get().rawRpcCallStreamingResponse({
    to: runner,
    endPoint: "jobrunner.v1.RunCheckpoint",
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  let out: WorkerRunOutcome = {
    processUid: "",
    host: "",
    state: "",
    resultJson: "",
    message: "",
    checkpointUid: "",
    needsGrant: "",
    output: "",
  };
  const reader = call.responses.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.data || value.data.length === 0) continue;
      const ev = JSON.parse(new TextDecoder().decode(value.data)) as RunCheckpointEvent;
      onEvent?.(ev);
      out = foldEvent(out, ev);
    }
  } finally {
    reader.releaseLock();
  }
  if (!out.state) {
    throw new Error(`the run on worker ${workerUid} ended without saying how the program stands (process ${out.processUid})`);
  }
  return out;
}
